import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import StudentRankingCard from './StudentRankingCard';
import StudentLeaderboardView from './StudentLeaderboardView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StudentDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [hangmanActivities, setHangmanActivities] = useState([]);
  const [linuxQuests, setLinuxQuests] = useState([]);
  const [textHunts, setTextHunts] = useState([]);
  const [odooActivities, setOdooActivities] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('quizzes'); // quizzes, hangman, terminal, texthunt, odoo, classroom, rankings, stats
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [pin, setPin] = useState('');
  const [classroomConnected, setClassroomConnected] = useState(false);
  const [classroomQuizzes, setClassroomQuizzes] = useState([]);
  const [loadingClassroom, setLoadingClassroom] = useState(false);
  const [hangmanFilter, setHangmanFilter] = useState('all'); // all, completed, pending

  useEffect(() => {
    fetchData();
    checkClassroomStatus();
  }, []);

  const fetchData = async () => {
    try {
      console.log('[DEBUG] Fetching data for student:', user.id);
      const [quizzesRes, hangmanRes, linuxRes, texthuntsRes, odooRes, resultsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/student/${user.id}/quizzes`),
        axios.get(`${BACKEND_URL}/api/student/${user.id}/hangman`),
        axios.get(`${BACKEND_URL}/api/student/${user.id}/linux-quests`),
        axios.get(`${BACKEND_URL}/api/student/${user.id}/texthunts`),
        axios.get(`${BACKEND_URL}/api/student/${user.id}/odoo-activities`).catch((e) => { console.error('[DEBUG] Odoo fetch error:', e); return { data: [] }; }),
        axios.get(`${BACKEND_URL}/api/student/${user.id}/results`)
      ]);
      
      console.log('[DEBUG] Odoo activities response:', odooRes.data);
      setQuizzes(quizzesRes.data);
      setHangmanActivities(hangmanRes.data);
      setLinuxQuests(linuxRes.data);
      setTextHunts(texthuntsRes.data);
      setOdooActivities(odooRes.data?.activities || []);
      setResults(resultsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkClassroomStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/classroom/student/status/${user.id}`);
      setClassroomConnected(response.data.connected);
      
      if (response.data.connected) {
        fetchClassroomQuizzes();
      }
    } catch (error) {
      console.error('Error checking Classroom status:', error);
    }
  };

  const fetchClassroomQuizzes = async () => {
    setLoadingClassroom(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/classroom/student/assigned-quizzes/${user.id}`);
      setClassroomQuizzes(response.data.quizzes || []);
    } catch (error) {
      console.error('Error fetching Classroom quizzes:', error);
    } finally {
      setLoadingClassroom(false);
    }
  };

  const handleConnectClassroom = async () => {
    try {
      // Store user ID and type for callback
      localStorage.setItem('user_id_for_classroom', user.id);
      localStorage.setItem('user_type_for_classroom', 'student');
      
      const response = await axios.get(`${BACKEND_URL}/api/classroom/auth-url`, {
        params: {
          teacher_id: user.id,
          user_type: 'student'
        }
      });
      
      // Open OAuth URL
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Error connecting to Classroom:', error);
      alert('Error al conectar con Google Classroom');
    }
  };

  const handleDisconnectClassroom = async () => {
    if (!window.confirm('¿Estás seguro de desconectar Google Classroom?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/classroom/student/disconnect/${user.id}`);
      setClassroomConnected(false);
      setClassroomQuizzes([]);
      alert('Desconectado de Google Classroom');
    } catch (error) {
      console.error('Error disconnecting Classroom:', error);
      alert('Error al desconectar');
    }
  };

  const handleStartQuiz = (quizId) => {
    navigate(`/quiz/${quizId}/individual`);
  };

  const handleStartClassroomQuiz = (quizId, courseworkId, courseId) => {
    navigate(`/quiz/${quizId}/individual?coursework_id=${courseworkId}&course_id=${courseId}`);
  };

  const handleJoinLive = () => {
    if (pin.trim().length < 4) {
      alert('Por favor ingresa un PIN válido');
      return;
    }
    navigate(`/play/${pin.trim()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - RESPONSIVE */}
      <header className="bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-6">
          <div className="flex flex-col gap-3">
            {/* Top Row - Title and Avatar */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">QuizMaster Student</h1>
                <p className="text-purple-100 mt-1 text-xs sm:text-sm md:text-base">Bienvenido, {user.name}!</p>
              </div>
              
              <div className="flex items-center gap-2">
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full" />
                )}
              </div>
            </div>
            
            {/* Bottom Row - Action Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Join Game Button */}
              <button
                onClick={() => setShowJoinModal(true)}
                className="flex-1 sm:flex-none px-3 sm:px-6 py-2 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="hidden sm:inline">Unirse con PIN</span>
                <span className="sm:hidden">PIN</span>
              </button>
              
              {/* Connect/Disconnect Classroom */}
              {!classroomConnected ? (
                <button
                  onClick={handleConnectClassroom}
                  className="flex-1 sm:flex-none px-3 sm:px-6 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z"/>
                  </svg>
                  <span className="hidden sm:inline">Conectar Classroom</span>
                  <span className="sm:hidden">Classroom</span>
                </button>
              ) : (
                <button
                  onClick={handleDisconnectClassroom}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition flex items-center justify-center gap-1 text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z"/>
                  </svg>
                  <span className="hidden sm:inline">Desconectar</span>
                  <span className="sm:hidden">❌</span>
                </button>
              )}
              
              <button
                onClick={onLogout}
                className="px-3 sm:px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition text-sm sm:text-base ml-auto"
              >
                <span className="hidden sm:inline">Cerrar Sesión</span>
                <span className="sm:hidden">🚪</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs - RESPONSIVE GRID */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-4 mb-6">
          <button
            onClick={() => setActiveTab('quizzes')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition text-xs sm:text-base ${
              activeTab === 'quizzes'
                ? 'bg-primary text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span className="block sm:inline">📝</span>
            <span className="hidden sm:inline"> Quizzes</span>
            <span className="block sm:inline text-xs sm:text-base"> ({quizzes.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('hangman')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition text-xs sm:text-base ${
              activeTab === 'hangman'
                ? 'bg-pink-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span className="block sm:inline">🎯</span>
            <span className="hidden sm:inline"> Ahorcados</span>
            <span className="block sm:inline text-xs sm:text-base"> ({hangmanActivities.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('terminal')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition text-xs sm:text-base ${
              activeTab === 'terminal'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span className="block sm:inline">💻</span>
            <span className="hidden sm:inline"> Terminal</span>
            <span className="block sm:inline text-xs sm:text-base"> ({linuxQuests.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('texthunt')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition text-xs sm:text-base ${
              activeTab === 'texthunt'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span className="block sm:inline">📚</span>
            <span className="hidden sm:inline"> TextHunt</span>
            <span className="block sm:inline text-xs sm:text-base"> ({textHunts.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('odoo')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition text-xs sm:text-base ${
              activeTab === 'odoo'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span className="block sm:inline">🏢</span>
            <span className="hidden sm:inline"> Odoo ERP</span>
            <span className="block sm:inline text-xs sm:text-base"> ({odooActivities.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('rankings')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition text-xs sm:text-base ${
              activeTab === 'rankings'
                ? 'bg-yellow-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span className="block sm:inline">🏆</span>
            <span className="hidden sm:inline"> Clasificación</span>
            <span className="sm:hidden text-xs"> Ranking</span>
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition text-xs sm:text-base ${
              activeTab === 'stats'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span className="block sm:inline">📊</span>
            <span className="hidden sm:inline"> Estadísticas</span>
            <span className="sm:hidden text-xs"> Stats</span>
          </button>
          {classroomConnected && (
            <button
              onClick={() => setActiveTab('classroom')}
              className={`col-span-2 sm:col-span-1 px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-base ${
                activeTab === 'classroom'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z"/>
              </svg>
              <span className="hidden sm:inline">Google Classroom</span>
              <span className="sm:hidden">Classroom</span>
              <span> ({classroomQuizzes.length})</span>
            </button>
          )}
        </div>

        {/* Quizzes Tab */}
        {activeTab === 'quizzes' && (
          <div className="space-y-6">
            {quizzes.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No hay quizzes disponibles
                </h3>
                <p className="text-gray-500">Espera a que tu profesor cree quizzes</p>
              </div>
            ) : (
              <>
                {/* Assigned Quizzes Section */}
                {quizzes.filter(q => !q.is_public).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-1 w-8 bg-blue-500 rounded"></div>
                      <h2 className="text-lg font-bold text-gray-800">Asignados a mis clases</h2>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                        {quizzes.filter(q => !q.is_public).length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {quizzes
                        .filter(quiz => !quiz.is_public)
                        .map((quiz) => (
                          <div key={quiz.id} className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                    📚 Asignado
                                  </span>
                                  <h3 className="text-lg font-bold text-gray-800">{quiz.title}</h3>
                                  {quiz.attempted && (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                      ✓ Completado
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{quiz.description || 'Sin descripción'}</p>
                                <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                                  <span>📝 {quiz.question_count} preguntas</span>
                                  {quiz.teacher_name && (
                                    <span className="text-xs">👤 {quiz.teacher_name}</span>
                                  )}
                                  {quiz.attempted && quiz.score !== null && (
                                    <span className="font-bold text-primary">{quiz.score} puntos</span>
                                  )}
                                </div>
                              </div>
                              {!quiz.attempted && (
                                <button
                                  onClick={() => navigate(`/quiz/${quiz.id}/individual`)}
                                  className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-purple-700 transition"
                                >
                                  Comenzar
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Public Quizzes Section */}
                {quizzes.filter(q => q.is_public).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-1 w-8 bg-purple-500 rounded"></div>
                      <h2 className="text-lg font-bold text-gray-800">Quizzes Públicos</h2>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                        {quizzes.filter(q => q.is_public).length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {quizzes
                        .filter(quiz => quiz.is_public)
                        .map((quiz) => (
                          <div key={quiz.id} className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                                    🌍 Público
                                  </span>
                                  <h3 className="text-lg font-bold text-gray-800">{quiz.title}</h3>
                                  {quiz.attempted && (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                      ✓ Completado
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{quiz.description || 'Sin descripción'}</p>
                                <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                                  <span>📝 {quiz.question_count} preguntas</span>
                                  {quiz.teacher_name && (
                                    <span className="text-xs">👤 {quiz.teacher_name}</span>
                                  )}
                                  {quiz.attempted && quiz.score !== null && (
                                    <span className="font-bold text-primary">{quiz.score} puntos</span>
                                  )}
                                </div>
                              </div>
                              {!quiz.attempted && (
                                <button
                                  onClick={() => navigate(`/quiz/${quiz.id}/individual`)}
                                  className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-purple-700 transition"
                                >
                                  Comenzar
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Hangman Tab */}
        {activeTab === 'hangman' && (
          <div className="space-y-6">
            {hangmanActivities.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="text-6xl mb-4">🎯</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No hay ahorcados disponibles
                </h3>
                <p className="text-gray-500">Espera a que tu profesor cree actividades de ahorcado</p>
              </div>
            ) : (
              <>
                {/* Assigned Activities Section */}
                {hangmanActivities.filter(a => !a.is_public).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-1 w-8 bg-blue-500 rounded"></div>
                      <h2 className="text-lg font-bold text-gray-800">Asignados a mis clases</h2>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                        {hangmanActivities.filter(a => !a.is_public).length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {hangmanActivities
                        .filter(activity => !activity.is_public)
                        .map((activity) => (
                          <div key={activity.id} className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                    📚 Asignado
                                  </span>
                                  <h3 className="text-lg font-bold text-gray-800">{activity.title}</h3>
                                  {activity.attempted && (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                      ✓ Completado
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{activity.description || 'Sin descripción'}</p>
                                <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                                  <span>📝 {activity.word_count} palabras</span>
                                  <span>💔 {activity.lives} vidas</span>
                                  {activity.teacher_name && (
                                    <span className="text-xs">👤 {activity.teacher_name}</span>
                                  )}
                                  {activity.attempted && activity.score !== null && (
                                    <span className="font-bold text-pink-600">{activity.score} puntos</span>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => navigate(`/hangman/${activity.id}/play`)}
                                className="px-6 py-3 bg-pink-600 text-white rounded-lg font-semibold hover:bg-pink-700 transition"
                              >
                                {activity.attempted ? 'Jugar de Nuevo' : 'Jugar'}
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Public Activities Section */}
                {hangmanActivities.filter(a => a.is_public).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-1 w-8 bg-purple-500 rounded"></div>
                      <h2 className="text-lg font-bold text-gray-800">Actividades Públicas</h2>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                        {hangmanActivities.filter(a => a.is_public).length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {hangmanActivities
                        .filter(activity => activity.is_public)
                        .map((activity) => (
                          <div key={activity.id} className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                                    🌍 Público
                                  </span>
                                  <h3 className="text-lg font-bold text-gray-800">{activity.title}</h3>
                                  {activity.attempted && (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                      ✓ Completado
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{activity.description || 'Sin descripción'}</p>
                                <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                                  <span>📝 {activity.word_count} palabras</span>
                                  <span>💔 {activity.lives} vidas</span>
                                  {activity.teacher_name && (
                                    <span className="text-xs">👤 {activity.teacher_name}</span>
                                  )}
                                  {activity.attempted && activity.score !== null && (
                                    <span className="font-bold text-pink-600">{activity.score} puntos</span>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => navigate(`/hangman/${activity.id}/play`)}
                                className="px-6 py-3 bg-pink-600 text-white rounded-lg font-semibold hover:bg-pink-700 transition"
                              >
                                {activity.attempted ? 'Jugar de Nuevo' : 'Jugar'}
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Terminal Quest Tab */}
        {activeTab === 'terminal' && (
          <div className="space-y-6">
            {linuxQuests.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="text-6xl mb-4">💻</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No hay Terminal Quests disponibles
                </h3>
                <p className="text-gray-500">Espera a que tu profesor cree actividades de Terminal Quest</p>
              </div>
            ) : (
              <>
                {/* Assigned Quests Section */}
                {linuxQuests.filter(q => !q.is_public).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-1 w-8 bg-blue-500 rounded"></div>
                      <h2 className="text-lg font-bold text-gray-800">Asignados a mis clases</h2>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                        {linuxQuests.filter(q => !q.is_public).length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {linuxQuests
                        .filter(quest => !quest.is_public)
                        .map((quest) => (
                          <div key={quest.id} className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                    📚 Asignado
                                  </span>
                                  <h3 className="text-lg font-bold text-gray-800">{quest.title}</h3>
                                  {quest.completed && (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                      ✓ Completado
                                    </span>
                                  )}
                                  {quest.started && !quest.completed && (
                                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                                      🔄 En progreso
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{quest.description || 'Sin descripción'}</p>
                                <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                                  <span>🎯 {quest.level_count} niveles</span>
                                  {quest.started && (
                                    <span>📊 Nivel {quest.current_level}/{quest.level_count}</span>
                                  )}
                                  {quest.teacher_name && (
                                    <span className="text-xs">👤 {quest.teacher_name}</span>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => navigate(`/terminal-quest/${quest.id}/play`)}
                                className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition"
                              >
                                {quest.completed ? 'Jugar de Nuevo' : quest.started ? 'Continuar' : 'Comenzar'}
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Public Quests Section */}
                {linuxQuests.filter(q => q.is_public).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-1 w-8 bg-green-500 rounded"></div>
                      <h2 className="text-lg font-bold text-gray-800">Actividades Públicas</h2>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                        {linuxQuests.filter(q => q.is_public).length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {linuxQuests
                        .filter(quest => quest.is_public)
                        .map((quest) => (
                          <div key={quest.id} className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                                    🌍 Público
                                  </span>
                                  <h3 className="text-lg font-bold text-gray-800">{quest.title}</h3>
                                  {quest.completed && (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                      ✓ Completado
                                    </span>
                                  )}
                                  {quest.started && !quest.completed && (
                                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                                      🔄 En progreso
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{quest.description || 'Sin descripción'}</p>
                                <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                                  <span>🎯 {quest.level_count} niveles</span>
                                  {quest.started && (
                                    <span>📊 Nivel {quest.current_level}/{quest.level_count}</span>
                                  )}
                                  {quest.teacher_name && (
                                    <span className="text-xs">👤 {quest.teacher_name}</span>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => navigate(`/terminal-quest/${quest.id}/play`)}
                                className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition"
                              >
                                {quest.completed ? 'Jugar de Nuevo' : quest.started ? 'Continuar' : 'Comenzar'}
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* TextHunt Tab */}
        {activeTab === 'texthunt' && (
          <div className="space-y-6">
            {textHunts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="text-6xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No hay TextHunts disponibles
                </h3>
                <p className="text-gray-500">Espera a que tu profesor cree actividades de TextHunt</p>
              </div>
            ) : (
              <>
                {/* Assigned TextHunts Section */}
                {textHunts.filter(th => !th.is_public).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-1 w-8 bg-blue-500 rounded"></div>
                      <h2 className="text-lg font-bold text-gray-800">Asignados a mis clases</h2>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                        {textHunts.filter(th => !th.is_public).length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {textHunts
                        .filter(th => !th.is_public)
                        .map((texthunt) => (
                          <div key={texthunt.id} className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                    📚 Asignado
                                  </span>
                                  <h3 className="text-lg font-bold text-gray-800">{texthunt.title}</h3>
                                  {texthunt.attempted && texthunt.is_graded && (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                      ✓ Corregido - {texthunt.score}pts
                                    </span>
                                  )}
                                  {texthunt.attempted && !texthunt.is_graded && (
                                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                                      ⏳ Pendiente de corrección
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{texthunt.description || 'Actividad de comprensión lectora'}</p>
                                <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                                  <span>❓ {texthunt.questions.length} preguntas</span>
                                  <span>🎯 {texthunt.total_points} puntos</span>
                                  {texthunt.teacher_name && (
                                    <span className="text-xs">👤 {texthunt.teacher_name}</span>
                                  )}
                                </div>
                              </div>
                              {texthunt.attempted && texthunt.is_graded ? (
                                <button
                                  onClick={() => navigate(`/texthunt/${texthunt.id}/result/${user.id}`)}
                                  className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition"
                                >
                                  📊 Ver Resultados
                                </button>
                              ) : texthunt.attempted && !texthunt.is_graded ? (
                                <div className="text-center">
                                  <div className="px-6 py-3 bg-gray-300 text-gray-600 rounded-lg font-semibold cursor-not-allowed">
                                    ⏳ Pendiente
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">Espera a que el profesor corrija</p>
                                </div>
                              ) : (
                                <button
                                  onClick={() => navigate(`/texthunt/${texthunt.id}/play`)}
                                  className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition"
                                >
                                  Comenzar
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Public TextHunts Section */}
                {textHunts.filter(th => th.is_public).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-1 w-8 bg-purple-500 rounded"></div>
                      <h2 className="text-lg font-bold text-gray-800">Actividades Públicas</h2>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                        {textHunts.filter(th => th.is_public).length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {textHunts
                        .filter(th => th.is_public)
                        .map((texthunt) => (
                          <div key={texthunt.id} className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                                    🌍 Público
                                  </span>
                                  <h3 className="text-lg font-bold text-gray-800">{texthunt.title}</h3>
                                  {texthunt.attempted && texthunt.is_graded && (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                      ✓ Corregido - {texthunt.score}pts
                                    </span>
                                  )}
                                  {texthunt.attempted && !texthunt.is_graded && (
                                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                                      ⏳ Pendiente de corrección
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{texthunt.description || 'Actividad de comprensión lectora'}</p>
                                <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                                  <span>❓ {texthunt.questions.length} preguntas</span>
                                  <span>🎯 {texthunt.total_points} puntos</span>
                                  {texthunt.teacher_name && (
                                    <span className="text-xs">👤 {texthunt.teacher_name}</span>
                                  )}
                                </div>
                              </div>
                              {texthunt.attempted && texthunt.is_graded ? (
                                <button
                                  onClick={() => navigate(`/texthunt/${texthunt.id}/result/${user.id}`)}
                                  className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition"
                                >
                                  📊 Ver Resultados
                                </button>
                              ) : texthunt.attempted && !texthunt.is_graded ? (
                                <div className="text-center">
                                  <div className="px-6 py-3 bg-gray-300 text-gray-600 rounded-lg font-semibold cursor-not-allowed">
                                    ⏳ Pendiente
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">Espera a que el profesor corrija</p>
                                </div>
                              ) : (
                                <button
                                  onClick={() => navigate(`/texthunt/${texthunt.id}/play`)}
                                  className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition"
                                >
                                  Comenzar
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Odoo Activities Tab */}
        {console.log('[DEBUG] Rendering odoo tab, activities count:', odooActivities.length)}
        {activeTab === 'odoo' && (
          <div className="space-y-6">
            {!Array.isArray(odooActivities) || odooActivities.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="text-6xl mb-4">🏢</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No hay Actividades Odoo disponibles
                </h3>
                <p className="text-gray-500">Espera a que tu profesor cree actividades de Odoo ERP</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Array.isArray(odooActivities) && odooActivities.map((activity) => (
                  <div key={activity.id} className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-indigo-500">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded">
                            🏢 Odoo ERP
                          </span>
                          <h3 className="text-lg font-bold text-gray-800">{activity.title}</h3>
                          {activity.completed && (
                            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                              ✓ Completado - {activity.score}pts
                            </span>
                          )}
                          {activity.in_progress && (
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                              ⏳ En progreso
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{activity.description}</p>
                        <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                          <span className={`px-2 py-1 rounded ${
                            activity.difficulty === 'beginner' ? 'bg-green-100 text-green-700' :
                            activity.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {activity.difficulty === 'beginner' ? '⭐ Principiante' :
                             activity.difficulty === 'intermediate' ? '⭐⭐ Intermedio' : '⭐⭐⭐ Avanzado'}
                          </span>
                          <span>🎯 {activity.objectives_count} objetivos</span>
                          <span>🏆 {activity.max_score} puntos</span>
                          {activity.time_limit_minutes > 0 && (
                            <span>⏱️ {activity.time_limit_minutes} min</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/odoo-activity/${activity.id}/play`)}
                        className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition"
                      >
                        {activity.in_progress ? 'Continuar' : 'Comenzar'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Google Classroom Quizzes */}
        {activeTab === 'classroom' && (
          <div className="space-y-4">
            {loadingClassroom ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="mt-4 text-gray-600">Cargando quizzes de Classroom...</p>
              </div>
            ) : classroomQuizzes.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="text-6xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No hay quizzes asignados en Classroom
                </h3>
                <p className="text-gray-500">Tus profesores no han asignado ningún quiz todavía</p>
              </div>
            ) : (
              classroomQuizzes.map((quiz) => (
                <div key={quiz.coursework_id} className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-bold text-gray-800">{quiz.title}</h3>
                        {quiz.completed_locally && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            ✓ Completado
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{quiz.description || 'Sin descripción'}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
                          </svg>
                          {quiz.course_name}
                        </span>
                        <span>📝 {quiz.question_count} preguntas</span>
                        {quiz.due_date && (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                            </svg>
                            Entrega: {new Date(quiz.due_date).toLocaleDateString('es-ES')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      {quiz.assigned_grade !== null && quiz.assigned_grade !== undefined ? (
                        <>
                          <div className="text-3xl font-bold text-primary">{quiz.assigned_grade}</div>
                          <div className="text-sm text-gray-500">/ {quiz.max_points}</div>
                        </>
                      ) : (
                        <div className="text-sm text-gray-500">Sin calificar</div>
                      )}
                    </div>
                  </div>
                  
                  {!quiz.completed_locally && (
                    <button
                      onClick={() => handleStartClassroomQuiz(quiz.quiz_id, quiz.coursework_id, quiz.course_id)}
                      className="w-full px-4 py-3 bg-success text-white rounded-lg font-semibold hover:bg-green-600 transition"
                    >
                      Comenzar Quiz
                    </button>
                  )}
                  
                  {quiz.completed_locally && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-green-700">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                          <span className="font-semibold">Completado en la app</span>
                        </div>
                        <div className="text-green-700 font-bold">{quiz.local_score} puntos</div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Rankings Tab */}
        {activeTab === 'rankings' && (
          <div>
            <StudentLeaderboardView user={user} />
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div>
            <StudentRankingCard user={user} />
          </div>
        )}
      </div>

      {/* Join Live Modal */}
      {showJoinModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowJoinModal(false)}
        >
          <div 
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Unirse a Sesión en Vivo</h3>
            <p className="text-gray-600 mb-6">
              Ingresa el PIN que te proporcionó tu profesor para unirte a la sesión en vivo
            </p>
            
            <input
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value.toUpperCase())}
              onKeyPress={(e) => e.key === 'Enter' && handleJoinLive()}
              placeholder="Ingresa el PIN"
              className="w-full px-6 py-4 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent mb-6"
              maxLength="6"
              autoFocus
            />
            
            <div className="flex gap-3">
              <button
                onClick={handleJoinLive}
                className="flex-1 px-6 py-3 bg-success text-white rounded-lg font-semibold hover:bg-green-600 transition"
              >
                Unirse
              </button>
              <button
                onClick={() => {
                  setShowJoinModal(false);
                  setPin('');
                }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentDashboard;
